import { Typography } from '@mui/material'
import React from 'react'
import { useSelector } from 'react-redux'
import { z } from 'zod'

import { SchemaOrg } from '@/__generated/es-api'
import Loader from '@/app/component/atom/loader'
import { Form } from '@/app/component/form'
import { ActionsContainer, StartAdornment } from '@/app/component/form/common'
import SubmitButton from '@/app/component/form/submit-button'
import { ROLES } from '@/app/definitions'
import { useGetOrganization } from '@/app/module/user/hooks/use-get-organization'
import { useUpdateOrganization } from '@/app/module/user/hooks/use-update-organization'
import { selectOrgId, selectUserRole } from '@/app/module/user/store/selectors'
import { selectUserLoading } from '@/app/module/user/store/ts/selectors'

type Props = {
  onSuccess?: () => void
}

const SpendNotifications: React.FC<Props> = ({ onSuccess }) => {
  const role = useSelector(selectUserRole)
  const orgId = useSelector(selectOrgId)
  const isUserLoading = useSelector(selectUserLoading)

  const isOwner = ROLES.OWNER.includes(role)
  const isOwnerOrManager = isOwner || ROLES.MANAGER.includes(role)

  const { data: organization, isLoading: isOrgLoading } = useGetOrganization(orgId)
  const [updateOrg, res] = useUpdateOrganization()

  const loading = isOrgLoading || isUserLoading || res.isLoading

  const defaultValues = React.useMemo<SpendFormValues>(
    () => ({
      balanceNotification: Number(organization?.balanceNotification || 0),
      maxDailySpend: Number(organization?.maxDailySpend || 0),
      spendNotification: Number(organization?.spendNotification || 0),
    }),
    [organization?.balanceNotification, organization?.maxDailySpend, organization?.spendNotification],
  )

  const fields = React.useMemo(
    () =>
      ({
        maxDailySpend: {
          type: 'number' as const,
          fieldProps: {
            name: 'maxDailySpend',
            label: 'Maximum Daily Spend',
            disabled: !isOwner,
            info: 'The maximum your account can spend on SMS, Voice, WhatsApp, and Airtime Top-ups in a day. Once you reach this limit, all subsequent messages will fail during that day. (Days start at midnight UTC. Set to 0 to disable.)',
            required: true,
            startAdornment: <StartAdornment position="start">$</StartAdornment>,
          },
        },
        balanceNotification: {
          type: 'number' as const,
          fieldProps: {
            name: 'balanceNotification',
            label: 'Low Balance Notification',
            disabled: !isOwnerOrManager,
            info: "We'll notify you when your account balance drops to this amount, so you can top up and avoid campaign disruptions.",
            required: true,
            startAdornment: <StartAdornment position="start">$</StartAdornment>,
          },
        },
        spendNotification: {
          type: 'number' as const,
          fieldProps: {
            name: 'spendNotification',
            label: 'Daily Spend Notification',
            disabled: !isOwnerOrManager,
            info: "We'll notify you when you've spent this amount in a day, so you notice if anything seems out of the ordinary. (Days start at midnight UTC. Set to 0 to disable.)",
            required: true,
            startAdornment: <StartAdornment position="start">$</StartAdornment>,
          },
        },
      }) as const,
    [isOwner, isOwnerOrManager],
  )

  const handleSubmit = React.useCallback(
    async (values: SpendFormValues) => {
      if (!organization) {
        return // should not happen but typescript needs this
      }

      const newItem: SchemaOrg = {
        ...organization,
        balanceNotification: values.balanceNotification,
        maxDailySpend: values.maxDailySpend,
        spendNotification: values.spendNotification,
      }
      const success = await updateOrg(orgId, newItem)
      if (success && onSuccess) {
        onSuccess()
      }
    },
    [organization, orgId, updateOrg, onSuccess],
  )

  if (!organization) {
    return <Loader />
  }

  return (
    <>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Configure notifications and limits to help manage your organization's spending
      </Typography>
      <Form
        defaultValues={defaultValues}
        fields={fields}
        id="edit-notifications"
        name="edit-notifications"
        schema={spendSchema}
        shouldReset
        onSubmit={handleSubmit}
        renderActions={({ isDirty }) => (
          <ActionsContainer>
            <SubmitButton isDirty={isDirty} isSubmitting={loading}>
              Save
            </SubmitButton>
          </ActionsContainer>
        )}
      />
    </>
  )
}

const spendSchema = z.object({
  maxDailySpend: z
    .number({ invalid_type_error: 'Must be a number', required_error: 'Must be a number' })
    .min(0, 'Must be a non-negative number')
    .describe('Maximum Daily Spend'),
  balanceNotification: z
    .number({ invalid_type_error: 'Must be a number', required_error: 'Must be a number' })
    .min(0, 'Must be a non-negative number')
    .describe('Low Balance Notification'),
  spendNotification: z
    .number({ invalid_type_error: 'Must be a number', required_error: 'Must be a number' })
    .min(0, 'Must be a non-negative number')
    .describe('Daily Spend Notification'),
})

type SpendFormValues = z.infer<typeof spendSchema>

export default SpendNotifications
